import React from 'react';
import { Button } from 'antd';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { ReactComponent as ExcelIcon } from '../../assets/excel-icon.svg';

interface props {
  isAllData: boolean;
  data: any;
  title: string;
}

const ExcelDownloadButton: React.FC<props> = ({ data, title, isAllData }) => {
  // ========== for all Data ==========
  const formatAllDataToExcel = (node: any): any => {
    const questions: any = {};
    const children = node.children ? node.children : node._children;

    children.forEach((question: any, index: number) => {
      const questionChildren = question.children
        ? question.children
        : question._children;
      const excelData: any[] = [];

      questionChildren.forEach((nodee: any) => {
        const category1 = nodee.name;
        const categories = nodee.children ? nodee.children : nodee._children;
        categories.forEach((category: any) => {
          const category2 = category.name;
          category.answers.forEach((answer: any) => {
            excelData.push({
              'Category 1': category1,
              'Category 2': category2,
              Answer: answer.answer,
            });
          });
        });
      });

      questions[`question${index}`] = {
        title,
        question: question.name,
        tableData: excelData,
      };
    });

    const timestamp = new Date();
    const formattedDate = timestamp.toLocaleDateString('de-AT', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });

    return { fileName: `${title}-${formattedDate}.xlsx`, questions };
  };

  const exportAllDataToExcel = async (
    fileData: any,
    fileName: string = 'answers.xlsx',
  ): Promise<void> => {
    const workbook = new ExcelJS.Workbook();

    const promisses = Object.entries(fileData).map(async (entry: any) => {
      const sheetName = entry[0];
      const sheetData = entry[1];

      const worksheet = workbook.addWorksheet(sheetName);

      // worksheet.columns = [{ key: 'Answers', width: 10 }];

      // ADD LOGO
      const response = await fetch(
        'https://api.meine-sicht.com/files/c24a8a5d2b870b93865e-1_Logo%20ohne%20Unterschrift.png',
      );
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();

      // Adiciona a imagem ao workbook
      const imageId = workbook.addImage({
        buffer: arrayBuffer,
        extension: 'png', // ou 'jpeg', dependendo do tipo da imagem
      });

      worksheet.addImage(imageId, {
        tl: { col: 0, row: 0 }, // top-left: coluna 2, linha 2 (base 0)
        ext: { width: 245, height: 70 },
      });

      // title
      // worksheet.mergeCells('A1:C1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = title;
      titleCell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
      titleCell.font = { bold: true, size: 16 };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'faeeff' },
      };

      // question
      // worksheet.mergeCells('A2:C2');
      const subtitleCell = worksheet.getCell('A2');
      subtitleCell.value = sheetData.question;
      subtitleCell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
      subtitleCell.font = { italic: true, size: 12 };
      subtitleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'faeeff' },
      };

      // timestamp
      // worksheet.mergeCells('A3:C3');
      const timestampCell = worksheet.getCell('A3');
      timestampCell.value = new Date();
      timestampCell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
      timestampCell.font = { italic: true, size: 12 };
      timestampCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'faeeff' },
      };

      // worksheet.mergeCells('A4:C4');
      worksheet.addRow(['']);

      worksheet.getColumn('A').width = 200;
      // worksheet.getColumn('B').width = 40;
      // worksheet.getColumn('C').width = 130;

      // header
      // const header = worksheet.addRow(Object.keys(sheetData.tableData[0]));
      worksheet.addRow(['Answers']).eachCell(cell => {
        const c = cell;
        c.alignment = { horizontal: 'center', vertical: 'middle' };
        c.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD2AD' },
        };
      });

      const uniqueAnswersSet = new Set<string>();
      sheetData.tableData.forEach((item: any) => {
        uniqueAnswersSet.add(item.Answer);
      });

      const uniqueAnswersArray = Array.from(uniqueAnswersSet);

      // data
      uniqueAnswersArray.forEach((answer: any) => {
        worksheet.addRow([answer]).eachCell(cell => {
          const c = cell;
          c.alignment = {
            wrapText: true,
          };
          c.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ffedde' },
          };
        });
        worksheet.addRow(['']);
      });
    });

    await Promise.all(promisses);

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
  };
  // ==================================

  // ========== for category data ==========
  const getAllAnswers = (node: any): any => {
    if (node.answers) {
      return node.answers.map((answer: any) => {
        return { Answer: answer.answer };
      });
    }

    const children = node.children ? node.children : node._children;
    return children.flatMap((child: any) => {
      return getAllAnswers(child);
    });
  };

  const formatDataToExcel = (node: any): any => {
    const excelData = getAllAnswers(data);

    const timestamp = new Date();
    const formattedDate = timestamp.toLocaleDateString('de-AT', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });

    let questionText = '';
    if (node.depth === 1) {
      questionText = node.name;
    }
    if (node.depth === 2) {
      questionText = node.parent.name;
    }

    return {
      title,
      fileName: `${node.name}-${formattedDate}.xlsx`,
      question: questionText,
      tableData: excelData,
    };
  };

  const exportToExcel = async (
    fileData: any,
    fileName: string = 'answers.xlsx',
  ): Promise<void> => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Bubble Data');

    // worksheet.columns = [
    //   { key: 'Category 1', width: 20 },
    //   { key: 'Category 2', width: 15 },
    //   { key: 'Answers', width: 10 },
    // ];

    // ADD TIMESTAMP
    // ADD LOGO
    const response = await fetch(
      'https://api.meine-sicht.com/files/c24a8a5d2b870b93865e-1_Logo%20ohne%20Unterschrift.png',
    );
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();

    // Adiciona a imagem ao workbook
    const imageId = workbook.addImage({
      buffer: arrayBuffer,
      extension: 'png', // ou 'jpeg', dependendo do tipo da imagem
    });

    worksheet.addImage(imageId, {
      tl: { col: 0, row: 0 }, // top-left: coluna 2, linha 2 (base 0)
      ext: { width: 245, height: 70 },
    });

    // title
    // worksheet.mergeCells('A1:C1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = title;
    titleCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };
    titleCell.font = { bold: true, size: 16 };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'faeeff' },
    };

    // question
    // worksheet.mergeCells('A2:C2');
    const subtitleCell = worksheet.getCell('A2');
    subtitleCell.value = fileData.question;
    subtitleCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };
    subtitleCell.font = { italic: true, size: 12 };
    subtitleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'faeeff' },
    };

    // timestamp
    // worksheet.mergeCells('A3:C3');
    const timestampCell = worksheet.getCell('A3');
    timestampCell.value = new Date();
    timestampCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
    };
    timestampCell.font = { italic: true, size: 12 };
    timestampCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'faeeff' },
    };

    // worksheet.mergeCells('A4:C4');
    worksheet.addRow(['']);

    worksheet.getColumn('A').width = 200;
    // worksheet.getColumn('B').width = 40;
    // worksheet.getColumn('C').width = 130;

    // header
    worksheet.addRow(['Answers']).eachCell(cell => {
      const c = cell;
      c.alignment = { horizontal: 'center', vertical: 'middle' };
      c.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD2AD' },
      };
    });

    const uniqueAnswersSet = new Set<string>();
    fileData.tableData.forEach((item: any) => {
      uniqueAnswersSet.add(item.Answer);
    });

    const uniqueAnswersArray = Array.from(uniqueAnswersSet);

    // data
    uniqueAnswersArray.forEach((answer: any) => {
      worksheet.addRow([answer]).eachCell(cell => {
        const c = cell;
        c.alignment = {
          wrapText: true,
        };
        c.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ffedde' },
        };
      });
      worksheet.addRow(['']);
    });

    // generate file and download
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
  };
  // =======================================

  const handleDownload = async (): Promise<void> => {
    if (isAllData) {
      const allDataToExcel = formatAllDataToExcel(data);
      await exportAllDataToExcel(
        allDataToExcel.questions,
        allDataToExcel.fileName,
      );
    } else {
      const dataToExcel = formatDataToExcel(data);
      await exportToExcel(dataToExcel, dataToExcel.fileName);
    }
  };

  return (
    <>
      {isAllData ? (
        <Button
          onClick={handleDownload}
          style={{
            alignSelf: 'center',
            marginTop: '15px',
            height: '4vh',
            background: '#fff0de',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ExcelIcon
            style={{ height: '3.5dvh', width: '1.5dvw', marginRight: '6px' }}
          />
          Export all data
        </Button>
      ) : (
        <Button
          onClick={handleDownload}
          style={{ background: 'transparent', height: '5vh', border: 'none' }}
        >
          <ExcelIcon
            style={{
              width: '3dvw',
              height: '5dvh',
            }}
          />
        </Button>
      )}
    </>
  );
};

export default ExcelDownloadButton;
